import React, { useCallback, useEffect, useState } from 'react';
import FadeCollapse from 'components/FadeCollapse/FadeCollapse';
import { Draggable } from 'react-beautiful-dnd';

import { type MenuOption, type PosOption, type ProductVariant } from 'generated/graphql';
import { posOptionQuery } from 'queries/Option';

import { useApolloClient } from '@apollo/client';

import { SelectMenu } from 'components/SelectMenu';
import EditableText from 'components/Inputs/EditableText';
import { useItemContext } from 'components/SingleItem/ItemContext';
import cuid from 'cuid';
import OptionGroupItem from './OptionGroupItem';

import styles from './OptionGroups.module.scss';
import { OptionSource } from '../../../types';
import { DOSHII_ITEM_VARIANT_IDENTIFIER, DOSHII_VIRTUAL_ITEM_IDENTIFIER } from '../../../utils';

interface Props {
  optionGroup?: MenuOption | undefined | null;
  handleChange: (option: MenuOption) => void;
  handleDelete: () => void;
  index: number;
  isSameOnAllApps: boolean;
  toggleOptionGroup: (posId: string) => void;
  isOpen: boolean;
}

const OptionGroup: React.FC<Props> = ({
  optionGroup,
  handleChange,
  handleDelete,
  index,
  isSameOnAllApps,
  isOpen,
  toggleOptionGroup
}) => {
  const { currentVenue, item, productLink, posProduct } = useItemContext();
  const [isDragDisabled, setIsDragDisabled] = useState(false);
  const { query } = useApolloClient();

  const itemThatDefinitelyExists = item!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

  const [posOption, setPosOption] = useState<PosOption | null>(null);
  const [isRequiredOption, setIsRequiredOption] = useState<boolean>(false);

  useEffect(() => {
    if (!item || !optionGroup || !item.customPosId) return;
    if (
      item.customPosId.startsWith(DOSHII_VIRTUAL_ITEM_IDENTIFIER) &&
      optionGroup.min === 1 &&
      optionGroup.max === 1 &&
      optionGroup.variants.every((v) => v.posId?.startsWith(DOSHII_ITEM_VARIANT_IDENTIFIER))
    ) {
      setIsRequiredOption(true);
    } else {
      setIsRequiredOption(false);
    }
  }, [item, optionGroup]);

  const compilePosOption = useCallback(async () => {
    if (!optionGroup || !currentVenue || !posProduct || optionGroup.clonedPosId) return;
    const productLinkOption = productLink?.options.find((option) => option.posId === optionGroup.posId);
    const posProductOption = posProduct.options.find((option) => option.posId === optionGroup.posId);
    if (
      optionGroup.source === OptionSource.APP_GLOBAL ||
      (productLinkOption && productLinkOption.source === OptionSource.APP_GLOBAL)
    ) {
      const posOptionResult = await query<{ posOption: PosOption }>({
        query: posOptionQuery,
        variables: {
          locationId: currentVenue.hashedId,
          posId: optionGroup.posId
        }
      });
      setPosOption({ ...posOptionResult.data.posOption, source: OptionSource.APP_GLOBAL });
    } else if (posProductOption) {
      setPosOption({ ...posProductOption, source: OptionSource.POS_PRODUCT });
    }
  }, [currentVenue, optionGroup, posProduct, productLink, query]);

  const compileClonedGlobalAppOption = useCallback(async () => {
    if (!optionGroup || !currentVenue || !optionGroup.clonedPosId) return;

    const {
      data: { posOption }
    } = await query<{ posOption: PosOption }>({
      query: posOptionQuery,
      variables: {
        locationId: currentVenue.hashedId,
        posId: optionGroup.clonedPosId
      }
    });
    const clonedGlobalAppOption: PosOption = {
      ...posOption,
      __typename: 'PosOption',
      variants: posOption.variants.map((variant) => {
        const posVariant = posOption.variants.find((posVariant) => posVariant.posId === variant.posId);
        return {
          ...variant,
          ...(posVariant && { price: posVariant.price }),
          __typename: 'PosVariant'
        };
      })
    };
    setPosOption({ ...clonedGlobalAppOption, source: OptionSource.APP_GLOBAL });
  }, [currentVenue, optionGroup, query]);

  const toggleDragOnEdits = useCallback((type: 'focus' | 'blur' | 'enter') => {
    if (type === 'focus') {
      setIsDragDisabled(true);
    } else {
      setIsDragDisabled(false);
    }
  }, []);

  useEffect(() => {
    compilePosOption();
  }, [compilePosOption]);

  useEffect(() => {
    compileClonedGlobalAppOption();
  }, [compileClonedGlobalAppOption]);

  if (!optionGroup) {
    return null;
  }

  const mergedVariants: ProductVariant[] = [
    ...optionGroup.variants,
    ...(posOption && posOption.variants
      ? posOption.variants
          .filter((posVariant) => {
            return !optionGroup.variants.some((variant) => variant.posId === posVariant.posId);
          })
          .map((variant): ProductVariant => {
            return {
              __typename: 'ProductVariant',
              mmId: cuid(),
              name: variant.name,
              posId: variant.posId,
              variablesByApp: itemThatDefinitelyExists.variablesByApp.map(({ app, surcharge }) => {
                // Traverse menus>products>options>variants and find the first variant matching posId.
                // Then find the corresponding app's availability from variablesByApp.
                // There is an edge case where the variant is not found in the current venue's menus.
                // In this case, the availability is set to 'unknown'.
                let variantAvailability = 'unknown';
                currentVenue?.menus?.forEach((menu) => {
                  menu.products?.forEach((product) => {
                    product.options?.forEach((option) => {
                      option.variants?.forEach((v) => {
                        if (v.posId === variant.posId) {
                          v.variablesByApp?.forEach((vApp) => {
                            const vAppId = vApp.app.id.startsWith('virtual') ? vApp.app.id.split('.')[1] : vApp.app.id;
                            const appId = app.id.startsWith('virtual') ? app.id.split('.')[1] : app.id;
                            if (vAppId === appId) {
                              variantAvailability = vApp.availability;
                            }
                          });
                        }
                      });
                    });
                  });
                });

                return {
                  __typename: 'VariantVariablesByApp',
                  app,
                  price: variant.price || 0,
                  surcharge,
                  availability: variantAvailability || 'unknown'
                };
              })
            };
          })
      : [])
  ];

  const { posId, name } = optionGroup;

  return (
    <Draggable isDragDisabled={isDragDisabled} draggableId={posId} index={index}>
      {(provided) => (
        <li
          key={posId}
          data-testid={`option-group-container-${posId}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style
          }}
          className={styles.ListItem}
        >
          <section aria-labelledby={optionGroup.posId}>
            <h4 id={optionGroup.posId}>
              <EditableText
                text={name}
                afterContent={
                  optionGroup?.variants && (
                    <span className={styles.ItemCount} data-testid="optionGroup-variants-text">
                      {optionGroup.variants.length} option
                      {optionGroup.variants.length === 1 ? '' : 's'}
                    </span>
                  )
                }
                onSubmit={(name: string) => {
                  handleChange({
                    ...optionGroup,
                    name
                  });
                }}
                onStateChange={toggleDragOnEdits}
                testId={`option-group-name-${posId}`}
              />
              <div className={styles.More}>
                <button
                  onClick={() => toggleOptionGroup(optionGroup.posId)}
                  className={`${styles.Toggle} ${isOpen ? styles.Open : ''}`}
                  type="button"
                  tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
                  data-testid="optionGroup-toggle"
                  aria-label="expand or collapse option group"
                >
                  <i className="mm-angle-down" />
                </button>

                {isRequiredOption ? null : (
                  <SelectMenu
                    options={[
                      {
                        content: (
                          <span className={styles.Red}>
                            <i className={`mm-trash-o ${styles.Icon}`} /> Delete group
                          </span>
                        ),
                        isRedHoverBackground: true,
                        value: 'delete-item',
                        type: 'button',
                        callback: handleDelete
                      }
                    ]}
                    defaultButtonContent={<i className="mm-ellipsis-v" aria-label="additional options" />}
                    buttonStyle={styles.CloseButton}
                    downIcon={null}
                    align="right"
                    size="small"
                  />
                )}
              </div>
            </h4>
            <FadeCollapse open={isOpen}>
              <OptionGroupItem
                posOption={posOption}
                handleChange={handleChange}
                optionGroup={optionGroup}
                variants={mergedVariants}
                isSameOnAllApps={isSameOnAllApps}
                isDragDisabled={isDragDisabled}
                toggleDragOnEdits={toggleDragOnEdits}
                isRequiredOption={isRequiredOption}
              />
            </FadeCollapse>
          </section>
        </li>
      )}
    </Draggable>
  );
};

export default OptionGroup;
